import type { HTMLAttributes } from "react";
import { useEffect, useRef } from "react";
import { cn } from "./lib/cn";
import { parseError } from "./lib/error";

declare global {
  interface Window {
    webkitAudioContext?: typeof AudioContext;
  }
}

export type LiveWaveformProps = HTMLAttributes<HTMLDivElement> & {
  active?: boolean;
  deviceId?: string;
  barWidth?: number;
  barHeight?: number;
  barGap?: number;
  barRadius?: number;
  barColor?: string;
  fadeEdges?: boolean;
  fadeWidth?: number;
  height?: string | number;
  sensitivity?: number;
  smoothingTimeConstant?: number;
  fftSize?: number;
  historySize?: number;
  updateRate?: number;
  mode?: "scrolling" | "static";
  onError?: (error: Error) => void;
};

/** Vendored ElevenLabs UI live microphone waveform, adapted to the AI Elements `cn` utility. */
export const LiveWaveform = ({
  active = false,
  deviceId,
  barWidth = 3,
  barHeight: baseBarHeight = 4,
  barGap = 1,
  barRadius = 1.5,
  barColor,
  fadeEdges = true,
  fadeWidth = 24,
  height = 64,
  sensitivity = 1,
  smoothingTimeConstant = 0.8,
  fftSize = 256,
  historySize = 60,
  updateRate = 30,
  mode = "static",
  onError,
  className,
  ...props
}: LiveWaveformProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const animationRef = useRef(0);
  const lastUpdateRef = useRef(0);
  const barsRef = useRef<number[]>([]);

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Synchronizes canvas size with its rendered container.
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) {
      return undefined;
    }
    const resizeObserver = new ResizeObserver(() => {
      const rect = container.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      canvas.getContext("2d")?.setTransform(dpr, 0, 0, dpr, 0, 0);
    });
    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, []);

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Synchronizes the browser microphone stream with recording state.
  useEffect(() => {
    if (!active) {
      return undefined;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      onError?.(new Error("Microphone input is not available in this browser."));
      return undefined;
    }
    let disposed = false;
    void navigator.mediaDevices
      .getUserMedia({
        audio: deviceId
          ? {
              deviceId: { exact: deviceId },
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
            }
          : { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      })
      .then((stream) => {
        if (disposed) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        const AudioContextConstructor = window.AudioContext ?? window.webkitAudioContext;
        const audioContext = new AudioContextConstructor();
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = fftSize;
        analyser.smoothingTimeConstant = smoothingTimeConstant;
        audioContext.createMediaStreamSource(stream).connect(analyser);
        audioContextRef.current = audioContext;
        analyserRef.current = analyser;
      })
      .catch((cause) => onError?.(parseError(cause)));
    return () => {
      disposed = true;
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      void audioContextRef.current?.close();
      audioContextRef.current = null;
      analyserRef.current = null;
    };
  }, [active, deviceId, fftSize, onError, smoothingTimeConstant]);

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Paints live Web Audio samples into the canvas animation loop.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return undefined;
    }
    const context = canvas.getContext("2d");
    if (!context) {
      return undefined;
    }
    const animate = (currentTime: number) => {
      const rect = canvas.getBoundingClientRect();
      if (active && analyserRef.current && currentTime - lastUpdateRef.current > updateRate) {
        lastUpdateRef.current = currentTime;
        const data = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteFrequencyData(data);
        const relevantData = data.slice(
          Math.floor(data.length * 0.05),
          Math.floor(data.length * 0.4),
        );
        if (mode === "scrolling") {
          const average =
            relevantData.reduce((sum, value) => sum + value, 0) /
            Math.max(1, relevantData.length) /
            255;
          barsRef.current.push(Math.max(0.05, Math.min(1, average * sensitivity)));
          if (barsRef.current.length > historySize) {
            barsRef.current.shift();
          }
        } else {
          const barCount = Math.floor(rect.width / (barWidth + barGap));
          const halfCount = Math.floor(barCount / 2);
          const half = Array.from({ length: halfCount }, (_, index) => {
            const dataIndex = Math.floor((index / halfCount) * relevantData.length);
            return Math.max(
              0.05,
              Math.min(1, ((relevantData[dataIndex] ?? 0) / 255) * sensitivity),
            );
          });
          barsRef.current = [...half].reverse().concat(half);
        }
      }
      context.clearRect(0, 0, rect.width, rect.height);
      const step = barWidth + barGap;
      const centerY = rect.height / 2;
      const color = barColor || getComputedStyle(canvas).color || "#000";
      barsRef.current.forEach((value, index) => {
        const renderedHeight = Math.max(baseBarHeight, value * rect.height * 0.8);
        context.fillStyle = color;
        context.globalAlpha = 0.4 + value * 0.6;
        context.beginPath();
        const x =
          mode === "scrolling"
            ? rect.width - (barsRef.current.length - index) * step
            : index * step;
        context.roundRect(x, centerY - renderedHeight / 2, barWidth, renderedHeight, barRadius);
        context.fill();
      });
      if (fadeEdges && fadeWidth > 0 && rect.width > 0) {
        const gradient = context.createLinearGradient(0, 0, rect.width, 0);
        const fadePercent = Math.min(0.3, fadeWidth / rect.width);
        gradient.addColorStop(0, "rgba(255,255,255,1)");
        gradient.addColorStop(fadePercent, "rgba(255,255,255,0)");
        gradient.addColorStop(1 - fadePercent, "rgba(255,255,255,0)");
        gradient.addColorStop(1, "rgba(255,255,255,1)");
        context.globalCompositeOperation = "destination-out";
        context.fillStyle = gradient;
        context.fillRect(0, 0, rect.width, rect.height);
        context.globalCompositeOperation = "source-over";
      }
      context.globalAlpha = 1;
      animationRef.current = requestAnimationFrame(animate);
    };
    animationRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationRef.current);
  }, [
    active,
    barColor,
    barGap,
    barRadius,
    barWidth,
    baseBarHeight,
    fadeEdges,
    fadeWidth,
    historySize,
    mode,
    sensitivity,
    updateRate,
  ]);

  return (
    <div
      aria-label="Live audio waveform"
      className={cn("relative w-full", className)}
      ref={containerRef}
      role="img"
      style={{ height: Number.isFinite(height) ? `${height}px` : height }}
      {...props}
    >
      <canvas aria-hidden="true" className="block h-full w-full" ref={canvasRef} />
    </div>
  );
};
